import React, { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useDoorQuestGame } from '../../hooks/useDoorQuestGame'
import { useGameAudio } from '../../hooks/useGameAudio'
import { useStudyStore } from '../../store/useStudyStore'
import { GameAnswer } from '../../types/database'
import { DoorQuestGameConfig } from '../../types'
import { DarkRoomScene } from './DarkRoomScene'
import { SuccessRoomScene } from './SuccessRoomScene'
import { MiniLeaderboard } from './GameLeaderboard'
import { PlayCircle, RotateCcw, Home, Loader2, RotateCw, Volume2, VolumeX } from 'lucide-react'
import './GameLandscapeStyles.css'

interface DoorQuestGameProps extends DoorQuestGameConfig {
  onExit?: () => void
  onMusicControlsReady?: (controls: { toggle: () => void; isPlaying: boolean }) => void
}

/**
 * Component Game chính - Phòng Bí Mật với 3 Cánh Cửa
 * 
 * Người chơi phải trả lời đúng câu hỏi bằng cách chọn cửa có đáp án đúng.
 * Chọn đúng: cửa mở, đi tiếp
 * Chọn sai: game over
 * Hoàn thành tất cả: chiến thắng
 */
export const DoorQuestGame: React.FC<DoorQuestGameProps> = ({
  lop,
  chuong,
  bai,
  minQuestions = 6,
  maxQuestions = 10,
  onExit,
  onMusicControlsReady
}) => {
  const {
    gameState,
    isLoading,
    startGame,
    selectDoor,
    resetGame,
    currentQuestion
  } = useDoorQuestGame({
    lop,
    chuong,
    bai,
    minQuestions,
    maxQuestions
  })

  // ⏱️ Timer - Đếm thời gian chơi game
  const [timeElapsed, setTimeElapsed] = React.useState(0)
  const [isTimerRunning, setIsTimerRunning] = React.useState(false)

  // 🎮 Study Store - Lưu kết quả game
  const { submitGame, startGame: startGameSession } = useStudyStore()

  // 📊 Track answers để lưu vào database
  const [gameAnswers, setGameAnswers] = React.useState<GameAnswer[]>([])
  const [gameStartTime, setGameStartTime] = React.useState<number>(0)

  // 📱 Orientation detection - Phát hiện chế độ dọc trên mobile
  const [isPortrait, setIsPortrait] = React.useState(false)
  const [isMobileLandscape, setIsMobileLandscape] = React.useState(false)
  
  useEffect(() => {
    const checkOrientation = () => {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      const isPortraitMode = window.innerHeight > window.innerWidth
      const isLandscapeMode = window.innerWidth > window.innerHeight
      
      setIsPortrait(isMobile && isPortraitMode)
      setIsMobileLandscape(isMobile && isLandscapeMode && window.innerHeight < 600)
    }

    checkOrientation()
    window.addEventListener('resize', checkOrientation)
    window.addEventListener('orientationchange', checkOrientation)

    return () => {
      window.removeEventListener('resize', checkOrientation)
      window.removeEventListener('orientationchange', checkOrientation)
    }
  }, [])

  // 🔄 Xử lý xoay màn hình thủ công
  const handleRotateScreen = async () => {
    try {
      // Thử request fullscreen
      if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen()
      }
      
      // Thử lock orientation (chỉ hoạt động trong fullscreen trên một số thiết bị)
      const screenOrientation = screen.orientation as any
      if (screenOrientation && screenOrientation.lock) {
        await screenOrientation.lock('landscape-primary').catch(() => {
          // Fallback: thử landscape thông thường
          return screenOrientation.lock('landscape').catch(() => {})
        })
      }
    } catch (err) {
      console.log('Rotation not supported:', err)
      // Không làm gì cả - người dùng phải xoay thủ công
    }
  }
  
  // 🔊 Trạng thái audio - Đã unlock chưa
  const [audioUnlocked, setAudioUnlocked] = React.useState(false)

  // Nhạc nền game
  const bgMusic = useGameAudio({
    backgroundMusic: '/sounds/game-background.mp3',
    volume: 0.3,
    loop: true,
    autoPlay: false
  })

  // Nhạc intro - Màn hình giới thiệu
  const introMusic = useGameAudio({
    backgroundMusic: '/sounds/game-intro.mp3',
    volume: 0.4,
    loop: true,
    autoPlay: true // Tự động phát
  })

  // Nhạc chiến thắng
  const victoryMusic = useGameAudio({
    backgroundMusic: '/sounds/game-victory.mp3',
    volume: 0.5,
    loop: false,
    autoPlay: false
  })

  // ⏱️ Timer Logic - Đếm thời gian khi chơi
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null

    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimeElapsed(prev => prev + 1)
      }, 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isTimerRunning])

  // ⏱️ Bắt đầu timer khi game active, dừng khi kết thúc
  useEffect(() => {
    if (gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost) {
      setIsTimerRunning(true)
    } else {
      setIsTimerRunning(false)
    }
  }, [gameState.isGameActive, gameState.isGameWon, gameState.isGameLost])

  // ⏱️ Reset timer khi bắt đầu game mới
  useEffect(() => {
    if (gameState.isGameActive && gameState.currentQuestionIndex === 0) {
      setTimeElapsed(0)
    }
  }, [gameState.isGameActive, gameState.currentQuestionIndex])

  // 📊 Track answer khi có feedback
  useEffect(() => {
    if (gameState.showFeedback && currentQuestion && gameState.selectedDoor !== null) {
      const isCorrect = gameState.selectedDoor === currentQuestion.correctIndex
      const answerTime = Math.floor(Math.random() * 10) + 5 // 5-15s per question (placeholder)
      
      const gameAnswer: GameAnswer = {
        questionId: currentQuestion.id,
        questionText: currentQuestion.question,
        userAnswer: currentQuestion.answers[gameState.selectedDoor],
        correctAnswer: currentQuestion.answers[currentQuestion.correctIndex],
        isCorrect,
        timeSpent: answerTime,
        roomNumber: gameState.currentQuestionIndex + 1
      }
      
      setGameAnswers(prev => [...prev, gameAnswer])
    }
  }, [gameState.showFeedback, gameState.selectedDoor, currentQuestion, gameState.currentQuestionIndex])

  // 💾 Lưu kết quả khi thắng game
  useEffect(() => {
    console.log('🔍 Save game useEffect triggered:', {
      isGameWon: gameState.isGameWon,
      gameAnswersLength: gameAnswers.length,
      score: gameState.score,
      totalQuestions: gameState.totalQuestions,
      timeElapsed
    })
    
    const saveGameResult = async () => {
      if (gameState.isGameWon && gameAnswers.length > 0) {
        console.log('💾 Conditions met, saving game result...')
        
        try {
          const gameId = `lop-${lop}-chuong-${chuong}-bai-${bai}`
          const lessonId = `${lop}-${chuong}-${bai}`
          const classId = `${lop}`
          const chapterId = `${chuong}`
          
          console.log('📝 Game data:', {
            gameId,
            lessonId,
            classId,
            chapterId,
            score: gameState.score,
            maxScore: gameState.totalQuestions,
            timeElapsed,
            answersCount: gameAnswers.length
          })
          
          await submitGame(
            gameId,
            lessonId,
            classId,
            chapterId,
            gameState.score,
            gameState.totalQuestions,
            timeElapsed,
            gameAnswers,
            true, // won = true
            gameState.currentQuestionIndex + 1 // roomsCompleted
          )
          
          console.log('✅ Game result saved successfully!')
        } catch (error) {
          console.error('❌ Failed to save game result:', error)
        }
      } else {
        if (!gameState.isGameWon) {
          console.log('⏳ Game not won yet')
        }
        if (gameAnswers.length === 0) {
          console.log('⚠️ No answers tracked yet')
        }
      }
    }
    
    saveGameResult()
  }, [gameState.isGameWon, gameAnswers, gameState.score, gameState.totalQuestions, timeElapsed, lop, chuong, bai, submitGame, gameState.currentQuestionIndex])

  // 🎮 Handler functions với timer reset
  const handleStartGame = async () => {
    setTimeElapsed(0)
    setGameAnswers([])
    setGameStartTime(Date.now())
    
    // Dừng nhạc intro NGAY khi bắt đầu game
    introMusic.pause()
    
    // Start game session
    const gameId = `lop-${lop}-chuong-${chuong}-bai-${bai}`
    const lessonId = `${lop}-${chuong}-${bai}`
    await startGameSession(gameId, lessonId)
    
    startGame()
    
    // Phát nhạc nền
    bgMusic.play()
  }

  const handleResetGame = () => {
    setTimeElapsed(0)
    setGameAnswers([])
    setGameStartTime(0)
    resetGame()
  }

  const handleExit = () => {
    setTimeElapsed(0)
    setIsTimerRunning(false)
    if (onExit) onExit()
  }

  // ⏱️ Format thời gian (mm:ss)
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Thêm class game mode nhưng không force orientation
  useEffect(() => {
    // Add game mode CSS class (không force landscape)
    document.body.classList.add('game-mode')

    return () => {
      document.body.classList.remove('game-mode')
    }
  }, [])

  // 🎵 Unlock audio khi user click lần đầu
  useEffect(() => {
    const unlockAudio = () => {
      setAudioUnlocked(true)
      // Phát nhạc intro sau khi unlock
      if (!gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost) {
        setTimeout(() => introMusic.play(), 100)
      }
    }

    document.addEventListener('click', unlockAudio, { once: true })
    document.addEventListener('touchstart', unlockAudio, { once: true })

    return () => {
      document.removeEventListener('click', unlockAudio)
      document.removeEventListener('touchstart', unlockAudio)
    }
  }, [])

  // Expose music controls to parent
  useEffect(() => {
    if (onMusicControlsReady) {
      onMusicControlsReady({
        toggle: () => {
          if (gameState.isGameActive) {
            bgMusic.toggle()
          } else if (!gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost) {
            introMusic.toggle()
          } else if (gameState.isGameWon) {
            victoryMusic.toggle()
          }
        },
        isPlaying: bgMusic.isPlaying || introMusic.isPlaying || victoryMusic.isPlaying
      })
    }
  }, [bgMusic.isPlaying, introMusic.isPlaying, victoryMusic.isPlaying, gameState.isGameActive, gameState.isGameWon, gameState.isGameLost, onMusicControlsReady])

  // Điều khiển nhạc theo trạng thái game
  useEffect(() => {
    // Chỉ xử lý music khi thắng hoặc thua, KHÔNG xử lý khi chơi
    if (gameState.isGameWon) {
      // Chiến thắng - Phát nhạc victory
      victoryMusic.play()
      bgMusic.pause()
      introMusic.pause()
    } else if (gameState.isGameLost) {
      // Thua - Dừng tất cả nhạc
      bgMusic.pause()
      introMusic.pause()
      victoryMusic.pause()
    }
    // Không xử lý isGameActive ở đây nữa - đã xử lý trong handleStartGame
  }, [gameState.isGameWon, gameState.isGameLost])

  // Xử lý intro music riêng
  useEffect(() => {
    if (!gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost) {
      // Màn hình intro - Chỉ play intro, pause các nhạc khác
      setTimeout(() => {
        introMusic.play()
        bgMusic.pause()
        victoryMusic.pause()
      }, 100) // Delay nhỏ để tránh race condition
    }
  }, [gameState.isGameActive, gameState.isGameWon, gameState.isGameLost])

  // Cleanup tất cả nhạc khi component unmount
  useEffect(() => {
    return () => {
      bgMusic.pause()
      introMusic.pause()
      victoryMusic.pause()
    }
  }, [])

  // 📱 Overlay yêu cầu xoay màn hình sang ngang (Portrait Mode Warning)
  if (isPortrait) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center z-[9999] overflow-hidden">
        {/* Background animated circles */}
        <motion.div
          className="absolute top-10 left-10 w-32 h-32 rounded-full bg-yellow-400/10"
          animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.1, 0.3] }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-40 h-40 rounded-full bg-purple-400/10"
          animate={{ scale: [1, 1.8, 1], opacity: [0.3, 0.1, 0.3] }}
          transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
        />

        <div className="text-center text-white px-6 space-y-6 relative z-10 max-w-md">
          {/* Icon điện thoại xoay - Animation liên tục */}
          <motion.div
            className="mx-auto relative"
            style={{ width: '120px', height: '120px' }}
          >
            {/* Vòng tròn xung quanh */}
            <motion.div
              className="absolute inset-0 rounded-full border-4 border-yellow-400/30"
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
              className="absolute inset-2 rounded-full border-4 border-yellow-400/20"
              animate={{ rotate: -360 }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            />
            
            {/* Icon xoay liên tục */}
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            >
              <RotateCw className="w-16 h-16 text-yellow-400" strokeWidth={2.5} />
            </motion.div>
          </motion.div>
          
          {/* Text content */}
          <motion.div
            className="space-y-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold text-yellow-400 drop-shadow-lg">
              📱 Xoay thiết bị sang ngang
            </h2>
            <p className="text-base text-gray-200">
              Game hoạt động tốt nhất ở chế độ ngang
            </p>
          </motion.div>

          {/* Animated phone visual */}
          <motion.div
            className="flex items-center justify-center gap-4 text-4xl py-4"
            animate={{ x: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span>📱</span>
            <motion.span
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              ➜
            </motion.span>
            <motion.span
              style={{ display: 'inline-block' }}
              animate={{ rotate: [-90, -90, -90] }}
            >
              📱
            </motion.span>
          </motion.div>

          {/* Instructions */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 space-y-2 border border-white/20">
            <p className="text-sm text-gray-300 font-medium">
              🔄 Cách xoay màn hình:
            </p>
            <ul className="text-xs text-gray-400 space-y-1.5 text-left">
              <li className="flex items-start gap-2">
                <span className="text-yellow-400 shrink-0">1.</span>
                <span>Tắt khóa xoay màn hình trên thiết bị</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400 shrink-0">2.</span>
                <span>Xoay điện thoại sang ngang (nằm ngang)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-yellow-400 shrink-0">3.</span>
                <span>Hoặc nhấn nút bên dưới để thử fullscreen</span>
              </li>
            </ul>
          </div>

          {/* Button thử fullscreen */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleRotateScreen}
            className="w-full bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-orange-500/50 transition-all flex items-center justify-center gap-2"
          >
            <RotateCw className="w-5 h-5" />
            <span>Thử Fullscreen & Xoay</span>
          </motion.button>

          {/* Note */}
          <motion.p
            className="text-xs text-gray-500 italic"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            💡 Một số thiết bị cần xoay thủ công
          </motion.p>
        </div>
      </div>
    )
  }

  // Màn hình chờ bắt đầu
  if (!gameState.isGameActive && !gameState.isGameWon && !gameState.isGameLost) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`w-full min-h-screen bg-gradient-to-b from-purple-900 via-indigo-900 to-black text-white flex items-center justify-center ${
          isMobileLandscape 
            ? 'overflow-y-auto p-2 py-3' 
            : 'overflow-y-auto p-3 py-8 sm:p-6'
        }`}
      >
        {/* 🔊 Thông báo bật âm thanh */}
        {!audioUnlocked && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`fixed left-1/2 transform -translate-x-1/2 bg-yellow-500/90 text-black rounded-full font-bold z-50 flex items-center gap-2 ${
              isMobileLandscape 
                ? 'top-1 px-3 py-1.5 text-xs' 
                : 'top-4 px-6 py-3 text-sm sm:text-base'
            }`}
          >
            <span className={isMobileLandscape ? 'text-sm' : 'text-xl'}>🔊</span>
            <span>{isMobileLandscape ? 'Nhấp để bật nhạc!' : 'Nhấp vào bất kỳ đâu để bật nhạc!'}</span>
          </motion.div>
        )}
        
        <div className={`w-full text-center ${
          isMobileLandscape 
            ? 'max-w-6xl space-y-2' 
            : 'max-w-2xl space-y-4 sm:space-y-6'
        }`}>
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.8 }}
          >
            <h1 className={`font-bold bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent ${
              isMobileLandscape 
                ? 'text-2xl mb-1' 
                : 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-3 sm:mb-4'
            }`}>
              🚪 Phòng Bí Mật 🚪
            </h1>
            {!isMobileLandscape && (
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-4 sm:mb-6">
                Hành Trình Qua Những Cánh Cửa Tri Thức
              </p>
            )}
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className={`bg-white/10 backdrop-blur-md rounded-xl border border-white/20 ${
              isMobileLandscape 
                ? 'p-3 space-y-2' 
                : 'sm:rounded-2xl p-4 sm:p-6 md:p-8 space-y-3 sm:space-y-4'
            }`}
          >
            <h2 className={`font-semibold text-yellow-300 ${
              isMobileLandscape 
                ? 'text-base mb-1' 
                : 'text-lg sm:text-xl md:text-2xl'
            }`}>
              📜 Luật chơi:
            </h2>
            <ul className={`text-left text-white ${
              isMobileLandscape 
                ? 'grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs' 
                : 'space-y-2 sm:space-y-3 text-sm sm:text-base'
            }`}>
              <li className="flex items-start gap-2">
                <span className={`shrink-0 ${isMobileLandscape ? 'text-sm' : 'text-lg sm:text-xl md:text-2xl'}`}>🏠</span>
                <span>{isMobileLandscape ? 'Nhốt trong phòng tối' : 'Bạn đang bị nhốt trong một căn phòng tối tăm, bí ẩn'}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`shrink-0 ${isMobileLandscape ? 'text-sm' : 'text-lg sm:text-xl md:text-2xl'}`}>❓</span>
                <span>{isMobileLandscape ? '3 cánh cửa, 3 đáp án' : 'Mỗi phòng có 3 cánh cửa với các đáp án khác nhau'}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`shrink-0 ${isMobileLandscape ? 'text-sm' : 'text-lg sm:text-xl md:text-2xl'}`}>✅</span>
                <span>{isMobileLandscape ? 'Đúng: cửa mở' : 'Chọn '}
                  {!isMobileLandscape && <strong>đúng</strong>}
                  {isMobileLandscape ? '' : ': cửa mở, bạn đi tiếp được'}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`shrink-0 ${isMobileLandscape ? 'text-sm' : 'text-lg sm:text-xl md:text-2xl'}`}>❌</span>
                <span>{isMobileLandscape ? 'Sai: cửa khóa' : 'Chọn '}
                  {!isMobileLandscape && <strong>sai</strong>}
                  {isMobileLandscape ? '' : ': tất cả cửa khóa, bạn bị nhốt lại!'}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`shrink-0 ${isMobileLandscape ? 'text-sm' : 'text-lg sm:text-xl md:text-2xl'}`}>🎯</span>
                <span>{isMobileLandscape ? `Đúng ${minQuestions} câu để thoát` : 'Trả lời đúng '}
                  {!isMobileLandscape && <strong>{minQuestions} câu</strong>}
                  {isMobileLandscape ? '' : ' để thoát ra phòng sáng'}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className={`shrink-0 ${isMobileLandscape ? 'text-sm' : 'text-lg sm:text-xl md:text-2xl'}`}>🎉</span>
                <span>{isMobileLandscape ? 'Thắng: bữa tiệc!' : 'Chiến thắng: thưởng thức bữa tiệc thịnh soạn!'}</span>
              </li>
            </ul>
          </motion.div>

          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleStartGame}
            disabled={isLoading}
            className={`bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white rounded-full font-bold shadow-2xl hover:shadow-orange-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto ${
              isMobileLandscape 
                ? 'px-6 py-2 text-sm gap-2' 
                : 'px-8 sm:px-10 md:px-12 py-3 sm:py-4 text-base sm:text-lg md:text-xl gap-2 sm:gap-3'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className={isMobileLandscape ? 'animate-spin w-4 h-4' : 'animate-spin w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7'} />
                <span>{isMobileLandscape ? 'Chờ...' : 'Đang chuẩn bị...'}</span>
              </>
            ) : (
              <>
                <PlayCircle className={isMobileLandscape ? 'w-4 h-4' : 'w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7'} />
                <span>{isMobileLandscape ? 'Bắt đầu' : 'Bắt đầu thử thách'}</span>
              </>
            )}
          </motion.button>

          {onExit && !isMobileLandscape && (
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={handleExit}
              className="text-white hover:text-gray-200 transition-colors flex items-center gap-2 mx-auto mt-4"
            >
              <Home size={20} />
              Quay lại
            </motion.button>
          )}
        </div>
      </motion.div>
    )
  }

  // Màn hình game đang chơi
  if (gameState.isGameActive && currentQuestion) {
    return (
      <div className="w-full h-screen overflow-hidden bg-gradient-to-b from-purple-900 via-indigo-900 to-black text-white flex flex-col">
        {/* Header - Responsive */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white/10 backdrop-blur-md px-2 sm:px-3 py-1.5 sm:py-2 flex items-center justify-between border-b border-white/20 shrink-0"
        >
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="text-xs sm:text-sm">🏠</span>
            <p className="text-xs sm:text-sm font-bold text-orange-400">
              <span className="hidden xs:inline">Phòng </span>{gameState.currentQuestionIndex + 1}/{gameState.totalQuestions}
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Score */}
            <p className="text-xs sm:text-sm font-bold text-yellow-400">{gameState.score} ✓</p>
          </div>
        </motion.div>
        
        {/* Cảnh 3D - Full height */}
        <div className="flex-1 relative overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={`question-${gameState.currentQuestionIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full h-full"
            >
              <DarkRoomScene
                answers={currentQuestion.answers}
                selectedDoor={gameState.selectedDoor}
                correctIndex={currentQuestion.correctIndex}
                showFeedback={gameState.showFeedback}
                onDoorClick={selectDoor}
                question={currentQuestion.question}
                isCorrect={gameState.selectedDoor === currentQuestion.correctIndex ? true : (gameState.selectedDoor !== null ? false : null)}
                isTransitioning={gameState.isTransitioning}
                roomOffset={gameState.roomOffset || 0}
              />
            </motion.div>
          </AnimatePresence>

          {/* ⏱️ Timer Display - Góc dưới phải */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`absolute z-50 pointer-events-none ${
              isMobileLandscape 
                ? 'bottom-2 right-2' 
                : 'bottom-4 right-4 sm:bottom-6 sm:right-6'
            }`}
          >
            <div className={`bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 text-white rounded-2xl shadow-2xl border-4 border-white/30 backdrop-blur-sm ${
              isMobileLandscape 
                ? 'px-3 py-2' 
                : 'px-6 py-4 sm:px-8 sm:py-6'
            }`}>
              <div className="text-center space-y-1">
                {!isMobileLandscape && (
                  <div className="text-xs sm:text-sm font-semibold opacity-90">Thời gian</div>
                )}
                <div className={`font-mono font-bold tracking-wider ${
                  isMobileLandscape 
                    ? 'text-lg' 
                    : 'text-3xl sm:text-4xl md:text-5xl'
                }`}>
                  {formatTime(timeElapsed)}
                </div>
                {!isMobileLandscape && (
                  <div className="text-xs opacity-75">⏱️</div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Feedback Overlay - Mép trên, giữa màn hình */}
          <AnimatePresence>
            {gameState.showFeedback && (
              <div
                style={{
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  right: '0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  zIndex: 60,
                  pointerEvents: 'none',
                  padding: '8px'
                }}
              >
                <motion.div
                  initial={{ scale: 0.5, opacity: 0, y: -30 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.5, opacity: 0, y: -30 }}
                  transition={{ type: 'spring', damping: 15, stiffness: 300 }}
                  className="w-full max-w-[85%] sm:max-w-[400px]"
                >
                  <div className={`px-4 sm:px-6 md:px-8 py-2 sm:py-3 rounded-lg sm:rounded-xl text-center font-bold text-sm sm:text-base md:text-lg shadow-2xl ${
                    gameState.feedbackType === 'correct'
                      ? 'bg-green-500 text-white'
                      : 'bg-red-500 text-white'
                  }`}
                    style={{ 
                      borderWidth: '2px',
                      borderStyle: 'solid',
                      borderColor: gameState.feedbackType === 'correct' ? '#86efac' : '#fca5a5'
                    }}
                  >
                    {gameState.feedbackMessage}
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Hint - Bottom Overlay - Responsive */}
          {!gameState.showFeedback && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute bottom-1 sm:bottom-2 left-1/2 transform -translate-x-1/2 text-center text-white text-[10px] sm:text-xs opacity-70 pointer-events-none px-2"
            >
              <p className="hidden sm:block">👆 Nhấp vào cánh cửa đúng · 🖱️ Kéo để xoay</p>
              <p className="sm:hidden">👆 Chọn cửa đúng</p>
            </motion.div>
          )}
        </div>
      </div>
    )
  }

  // Màn hình chiến thắng
  if (gameState.isGameWon) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full h-screen overflow-y-auto bg-gradient-to-b from-yellow-100 via-orange-100 to-yellow-200 p-4"
      >
        <div className="max-w-6xl mx-auto space-y-6">
          <motion.div
            initial={{ scale: 0.8, y: -50 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ type: 'spring', duration: 0.8 }}
            className="text-center space-y-4"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-orange-600">
              🎉 CHIẾN THẮNG! 🎉
            </h1>
              <p className="text-2xl md:text-3xl text-orange-800">
              Bạn đã vượt qua {gameState.totalQuestions} cánh cửa tri thức!
            </p>
            
            {/* ⏱️ Bảng thời gian lớn nổi bật */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: 'spring', damping: 10 }}
              className="bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 text-white rounded-3xl p-8 sm:p-10 md:p-12 shadow-2xl border-4 border-white/40 inline-block min-w-[280px] sm:min-w-[350px]"
            >
              <div className="text-center space-y-3 sm:space-y-4">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-4xl sm:text-5xl">⏱️</span>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold">Thời gian</h3>
                </div>
                <div className="font-mono font-black text-5xl sm:text-6xl md:text-7xl tracking-wider drop-shadow-lg">
                  {formatTime(timeElapsed)}
                </div>
                <div className="text-sm sm:text-base md:text-lg opacity-90 font-semibold">
                  {timeElapsed < 120 ? '⚡ Nhanh như chớp!' : 
                   timeElapsed < 300 ? '👍 Tốt lắm!' : 
                   '💪 Kiên trì đáng khen!'}
                </div>
              </div>
            </motion.div>

            {/* Điểm số */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 inline-block mt-4">
              <p className="text-xl text-orange-700">
                Điểm số: <span className="text-3xl font-bold text-orange-600">{gameState.score}/{gameState.totalQuestions}</span>
              </p>
            </div>
          </motion.div>

          {/* 🏆 Mini Leaderboard - Top kết quả */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-2xl mx-auto"
          >
            <MiniLeaderboard 
              gameId={`lop-${lop}-chuong-${chuong}-bai-${bai}`}
              limit={5}
            />
          </motion.div>

          {/* Hướng dẫn tiếp tục */}
          {onExit && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-blue-100 border-2 border-blue-400 rounded-xl p-4 max-w-2xl mx-auto"
            >
              <p className="text-blue-800 text-lg font-semibold text-center">
                📚 Kiến thức đã được củng cố! Hãy tiếp tục với:
              </p>
              <div className="flex items-center justify-center gap-4 mt-2 text-blue-700 flex-wrap">
                <span>📹 Video YouTube</span>
                <span>•</span>
                <span>📝 Trắc nghiệm</span>
                <span>•</span>
                <span>🔬 Mô phỏng</span>
              </div>
            </motion.div>
          )}

          <SuccessRoomScene />

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex gap-4 justify-center flex-wrap"
          >
            {/* Nút chính - Tiếp tục học */}
            {onExit && (
              <button
                onClick={handleExit}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-10 py-5 rounded-full text-xl font-bold shadow-2xl hover:shadow-3xl transition-all transform hover:scale-110 flex items-center gap-3 animate-pulse"
              >
                <Home size={28} />
                Tiếp tục học
              </button>
            )}
            
            {/* Nút phụ - Chơi lại */}
            <button
              onClick={handleStartGame}
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <PlayCircle size={24} />
              Chơi lại
            </button>
          </motion.div>
        </div>
      </motion.div>
    )
  }

  // Màn hình thua cuộc
  if (gameState.isGameLost) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-full h-screen overflow-hidden bg-gradient-to-b from-gray-900 via-red-900 to-black text-white flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.8 }}
          className="max-w-2xl w-full text-center space-y-8"
        >
          <h1 className="text-5xl md:text-7xl font-bold text-red-500">
            😢 Thất bại!
          </h1>
          <p className="text-2xl md:text-3xl text-gray-300">
            Bạn đã bị nhốt trong phòng tối...
          </p>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
            <p className="text-xl">
              Đã vượt qua: <span className="text-2xl font-bold text-yellow-400">{gameState.score}/{gameState.totalQuestions}</span> phòng
            </p>
          </div>

          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={handleStartGame}
              className="bg-gradient-to-r from-red-500 to-orange-600 text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <RotateCcw size={24} />
              Thử lại
            </button>
            <button
              onClick={handleResetGame}
              className="bg-gradient-to-r from-gray-600 to-gray-700 text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <Home size={24} />
              Trở về
            </button>
            {onExit && (
              <button
                onClick={handleExit}
                className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
              >
                <Home size={24} />
                Thoát
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    )
  }

  return null
}

